import type { DeviceConfigSnapshot } from "@shared/admin";
import type { DeviceOperatorSnapshot } from "@shared/identity";
import { hashDeviceConfigSnapshot } from "@shared/device-config";
import type { EventMutation, LocalPrintJob, MenuItem, Order, OrderEvent, OrderMutation, OutboxMutation, SyncConflict } from "@shared/contracts";

const DEFAULT_MENU_SEED: readonly [string, string, string, number][] = [
  ["thin-vermicelli", "Thin (vermicelli)", "Main dish", 0],
  ["flat", "Flat", "Main dish", 0],
  ["udon", "Udon", "Main dish", 0],
  ["hokkien", "Hokkien", "Main dish", 0],
  ["rice", "Rice", "Main dish", 0],
  ["pad-krap-pow", "Pad Krap Pow", "Main dish", 0],
  ["green-chicken-curry", "Green chicken curry", "Main dish", 949],
  ["extra-filling", "Extra filling", "Main dish", 0],
  ["chicken-satay", "Chicken satay", "Starter", 450],
  ["duck-spring-roll", "Duck spring roll", "Starter", 450],
  ["chicken-dumpling", "Chicken dumpling", "Starter", 450],
  ["veg-spring-roll", "Veg spring roll", "Starter", 450],
  ["thai-tom-yam", "Thai tom yam", "Soup", 450],
  ["udon-beef-soup", "Udon beef soup", "Soup", 450],
  ["wanton-soup", "Wanton soup", "Soup", 450],
  ["canned-drink", "Canned drink", "Drinks", 150],
  ["bottled-drink", "Bottled drink", "Drinks", 195],
  ["kids-drink", "Kids drink", "Drinks", 100],
  ["water", "Water", "Drinks", 100],
];

const noodleFillingOptions = ["Chicken", "Beef", "Pork", "Tofu", "Veg", "Prawns", "Sea food", "No filling"]
  .map((name, index) => ({ id: `filling-${index}`, name, priceMinor: name === "Prawns" || name === "Sea food" ? 949 : 849, active: true }));
const padFillingOptions = ["Chicken", "Pork", "Beef", "Prawn", "Sea food"]
  .map((name, index) => ({ id: `pad-filling-${index}`, name, priceMinor: index > 2 ? 1049 : 949, active: true }));
const sauceOptions = ["Chow mein", "Teriyaki", "BBQ", "Sukiyaki", "Kung pao", "Pad Thai", "Singapore", "Indonesian hot", "Pad kee mao", "No sauce"]
  .map((name, index) => ({ id: `sauce-${index}`, name, priceMinor: 0, active: true }));
const commonExtraOptions = ["Ext chicken", "Ext beef", "Ext pork", "Ext tofu", "Ext veg", "Ext prawn", "Ext seafood", "Ext noodle", "Ext sauce", "Large portion"]
  .map((name, index) => ({ id: `extra-${index}`, name, priceMinor: index > 4 && index < 7 ? 200 : index > 6 ? 50 : 100, active: true }));

export const DEFAULT_MENU: MenuItem[] = DEFAULT_MENU_SEED.map(([id, name, category, priceMinor]) => ({
  id,
  name,
  category,
  priceMinor: Number(priceMinor),
  currency: "GBP" as const,
  active: true,
  updatedAt: "2026-08-09T00:00:00.000Z",
  optionGroups: ["Main dish", "Starter"].includes(category)
    ? [
        ...(category === "Main dish" && id !== "extra-filling" && id !== "green-chicken-curry" ? [{ id: "filling", name: "Filling", required: true, options: id === "pad-krap-pow" ? padFillingOptions : noodleFillingOptions }] : []),
        ...(id.startsWith("thin") || id === "flat" || id === "udon" || id === "hokkien" || id === "rice" ? [{ id: "sauce", name: "Sauce", options: sauceOptions }] : []),
        { id: "extras", name: "Extras", multiple: true, options: commonExtraOptions },
      ]
    : undefined,
}));

type RecordByStore = {
  menu: MenuItem;
  meta: { key: string; value: string };
  orders: Order;
  outbox: OutboxMutation;
  events: OrderEvent;
  printJobs: LocalPrintJob;
  conflicts: SyncConflict;
  deviceConfig: { key: "snapshot"; snapshot: DeviceConfigSnapshot };
  operatorDirectory: DeviceOperatorSnapshot;
};

type StoreName = keyof RecordByStore;
type Write = { [K in StoreName]: { store: K; value: RecordByStore[K] } }[StoreName];

function recordKey<K extends StoreName>(store: K, value: RecordByStore[K]): string {
  if (store === "outbox") return (value as OutboxMutation).mutationId;
  if (store === "meta") return (value as { key: string }).key;
  if (store === "deviceConfig") return (value as { key: string }).key;
  if (store === "operatorDirectory") return (value as DeviceOperatorSnapshot).actorId;
  return (value as { id: string }).id;
}

export interface RecordStore {
  list<K extends StoreName>(store: K): Promise<RecordByStore[K][]>;
  putMany(writes: Write[]): Promise<void>;
  deleteMany<K extends StoreName>(store: K, keys: string[]): Promise<void>;
  replace(writes: Write[], deletes: Partial<{ [K in StoreName]: string[] }>): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryRecordStore implements RecordStore {
  private readonly records: { [K in StoreName]: Map<string, RecordByStore[K]> } = {
    menu: new Map(),
    meta: new Map(),
    orders: new Map(),
    outbox: new Map(),
    events: new Map(),
    printJobs: new Map(),
    conflicts: new Map(),
    deviceConfig: new Map(),
    operatorDirectory: new Map(),
  };

  async list<K extends StoreName>(store: K): Promise<RecordByStore[K][]> {
    return [...this.records[store].values()];
  }

  async putMany(writes: Write[]): Promise<void> {
    for (const write of writes) {
      const key = recordKey(write.store, write.value);
      this.records[write.store].set(key, write.value as never);
    }
  }

  async deleteMany<K extends StoreName>(store: K, keys: string[]): Promise<void> {
    for (const key of keys) this.records[store].delete(key);
  }

  async replace(writes: Write[], deletes: Partial<{ [K in StoreName]: string[] }>): Promise<void> {
    for (const [store, keys] of Object.entries(deletes) as Array<[StoreName, string[] | undefined]>) {
      for (const key of keys ?? []) this.records[store].delete(key);
    }
    await this.putMany(writes);
  }

  async clear(): Promise<void> {
    for (const store of Object.values(this.records)) store.clear();
  }
}

export class IndexedDbRecordStore implements RecordStore {
  private readonly dbPromise: Promise<IDBDatabase>;

  constructor(dbName = "posnext-local") {
    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 4);
      request.onupgradeneeded = () => {
        const db = request.result;
        for (const store of ["menu", "meta", "orders", "outbox", "events", "printJobs", "conflicts", "deviceConfig", "operatorDirectory"] as StoreName[]) {
          if (!db.objectStoreNames.contains(store)) db.createObjectStore(store);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Unable to open IndexedDB"));
    });
  }

  async list<K extends StoreName>(store: K): Promise<RecordByStore[K][]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const request = db.transaction(store, "readonly").objectStore(store).getAll();
      request.onsuccess = () => resolve(request.result as RecordByStore[K][]);
      request.onerror = () => reject(request.error ?? new Error("Unable to read IndexedDB"));
    });
  }

  async putMany(writes: Write[]): Promise<void> {
    if (writes.length === 0) return;
    const db = await this.dbPromise;
    const stores = [...new Set(writes.map((write) => write.store))];
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(stores, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Unable to write IndexedDB"));
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
      for (const write of writes) {
        const key = recordKey(write.store, write.value);
        transaction.objectStore(write.store).put(write.value, key);
      }
    });
  }

  async deleteMany<K extends StoreName>(store: K, keys: string[]): Promise<void> {
    if (keys.length === 0) return;
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(store, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Unable to delete from IndexedDB"));
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB delete transaction aborted"));
      const objectStore = transaction.objectStore(store);
      for (const key of keys) objectStore.delete(key);
    });
  }

  async replace(writes: Write[], deletes: Partial<{ [K in StoreName]: string[] }>): Promise<void> {
    const db = await this.dbPromise;
    const stores = [...new Set([
      ...writes.map((write) => write.store),
      ...Object.keys(deletes),
    ])] as StoreName[];
    if (stores.length === 0) return;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(stores, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Unable to replace IndexedDB snapshot"));
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB snapshot replacement aborted"));
      for (const [store, keys] of Object.entries(deletes) as Array<[StoreName, string[] | undefined]>) {
        for (const key of keys ?? []) transaction.objectStore(store).delete(key);
      }
      for (const write of writes) {
        const key = recordKey(write.store, write.value);
        transaction.objectStore(write.store).put(write.value, key);
      }
    });
  }

  async clear(): Promise<void> {
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const stores = [...db.objectStoreNames] as StoreName[];
      const transaction = db.transaction(stores, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Unable to clear IndexedDB"));
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB clear transaction aborted"));
      for (const store of stores) transaction.objectStore(store).clear();
    });
  }

  async close(): Promise<void> {
    (await this.dbPromise).close();
  }
}

export class PosLocalRepository {
  constructor(private readonly store: RecordStore) {}

  async ensureMenu(): Promise<MenuItem[]> {
    const existing = await this.store.list("menu");
    const existingById = new Map(existing.map((item) => [item.id, item]));
    const seedsToApply = DEFAULT_MENU.filter((item) => {
      const current = existingById.get(item.id);
      return !current || current.updatedAt === item.updatedAt;
    });
    if (seedsToApply.length > 0) await this.store.putMany(seedsToApply.map((item) => ({ store: "menu" as const, value: item })));
    const updatedById = new Map(seedsToApply.map((item) => [item.id, item]));
    return [...existing, ...seedsToApply.filter((item) => !existingById.has(item.id))]
      .map((item) => updatedById.get(item.id) ?? item)
      .map((item) => ({ ...item }));
  }

  async getDeviceConfigSnapshot(): Promise<DeviceConfigSnapshot | null> {
    return (await this.store.list("deviceConfig")).find((record) => record.key === "snapshot")?.snapshot ?? null;
  }

  async listDeviceOperators(): Promise<DeviceOperatorSnapshot[]> {
    return this.store.list("operatorDirectory");
  }

  async applyDeviceConfigSnapshot(snapshot: DeviceConfigSnapshot): Promise<void> {
    if (!snapshot.configRevision || !snapshot.snapshotHash || !snapshot.device?.storeId || !snapshot.device.deviceId) {
      throw new Error("Device Config snapshot identity is invalid");
    }
    const existing = await this.getDeviceConfigSnapshot();
    if (existing && existing.configRevision > snapshot.configRevision) {
      throw new Error(`Device Config revision regressed (${existing.configRevision} -> ${snapshot.configRevision})`);
    }
    if (existing?.configRevision === snapshot.configRevision && existing.snapshotHash !== snapshot.snapshotHash) {
      throw new Error("Device Config revision changed its snapshot hash");
    }
    if (existing?.snapshotHash === snapshot.snapshotHash) {
      if (existing.configRevision === snapshot.configRevision) return;
      await this.store.replace([
        { store: "deviceConfig", value: { key: "snapshot", snapshot } },
        { store: "meta", value: { key: "device.config.revision", value: String(snapshot.configRevision) } },
        { store: "meta", value: { key: "device.config.hash", value: snapshot.snapshotHash } },
      ], {});
      return;
    }
    if (await hashDeviceConfigSnapshot(snapshot) !== snapshot.snapshotHash) {
      throw new Error("Device Config snapshot hash does not match its content");
    }

    const existingMenu = await this.store.list("menu");
    const existingOperators = await this.store.list("operatorDirectory");
    const menuItems = snapshot.menu?.items ?? [];
    await this.store.replace([
      { store: "deviceConfig", value: { key: "snapshot", snapshot } },
      ...menuItems.map((item) => ({ store: "menu" as const, value: item })),
      ...snapshot.operators.map((operator) => ({ store: "operatorDirectory" as const, value: operator })),
      { store: "meta", value: { key: "device.initialized", value: "true" } },
      { store: "meta", value: { key: "device.config.revision", value: String(snapshot.configRevision) } },
      { store: "meta", value: { key: "device.config.hash", value: snapshot.snapshotHash } },
    ], {
      menu: existingMenu.map((item) => item.id),
      operatorDirectory: existingOperators.map((operator) => operator.actorId),
    });
  }

  saveMenuItem(item: MenuItem): Promise<void> {
    return this.store.putMany([{ store: "menu", value: item }]);
  }

  listOrders(): Promise<Order[]> {
    return this.store.list("orders");
  }

  listPendingMutations(): Promise<OutboxMutation[]> {
    return this.store.list("outbox");
  }

  listEvents(): Promise<OrderEvent[]> {
    return this.store.list("events");
  }

  listConflicts(): Promise<SyncConflict[]> {
    return this.store.list("conflicts");
  }

  listPrintJobs(): Promise<LocalPrintJob[]> {
    return this.store.list("printJobs");
  }

  async getMeta(key: string): Promise<string | undefined> {
    return (await this.store.list("meta")).find((item) => item.key === key)?.value;
  }

  setMeta(key: string, value: string): Promise<void> {
    if (!key) throw new Error("Meta key is required");
    return this.store.putMany([{ store: "meta", value: { key, value } }]);
  }

  async nextOrderNumber(storeId: string, businessDate: string): Promise<string> {
    if (!storeId || !businessDate) throw new Error("storeId and businessDate are required for order numbering");
    const key = `order-counter:${storeId}:${businessDate}`;
    const existing = (await this.store.list("meta")).find((item) => item.key === key);
    const orders = await this.store.list("orders");
    const highestPersisted = orders
      .filter((order) => order.storeId === storeId && order.businessDate === businessDate)
      .map((order) => Number(order.orderNo))
      .filter((orderNo) => Number.isSafeInteger(orderNo) && orderNo > 0)
      .reduce((highest, orderNo) => Math.max(highest, orderNo), 0);
    const highestRecorded = Number(existing?.value ?? 0);
    const next = Math.max(highestPersisted, Number.isSafeInteger(highestRecorded) ? highestRecorded : 0) + 1;
    if (!Number.isSafeInteger(next) || next < 1) throw new Error("Order number counter is invalid");
    await this.store.putMany([{ store: "meta", value: { key, value: String(next) } }]);
    return String(next).padStart(3, "0");
  }

  async getSyncCursor(): Promise<number> {
    const value = (await this.store.list("meta")).find((item) => item.key === "sync.cursor")?.value;
    const cursor = Number(value ?? 0);
    return Number.isSafeInteger(cursor) && cursor >= 0 ? cursor : 0;
  }

  setSyncCursor(cursor: number): Promise<void> {
    if (!Number.isSafeInteger(cursor) || cursor < 0) throw new Error("Sync cursor must be a non-negative integer");
    return this.getSyncCursor().then((existing) => {
      if (cursor < existing) throw new Error(`Sync cursor cannot move backwards (${existing} -> ${cursor})`);
      if (cursor === existing) return;
      return this.store.putMany([{ store: "meta", value: { key: "sync.cursor", value: String(cursor) } }]);
    });
  }

  acknowledgeMutations(mutationIds: string[]): Promise<void> {
    return this.store.deleteMany("outbox", mutationIds);
  }

  async upsertRemoteOrders(orders: Order[]): Promise<void> {
    const localById = new Map((await this.store.list("orders")).map((order) => [order.id, order]));
    const pending = await this.store.list("outbox");
    const writes: Array<{ store: "orders"; value: Order } | { store: "conflicts"; value: SyncConflict }> = [];
    for (const order of orders) {
      const local = localById.get(order.id);
      const pendingLocalMutation = pending.some((mutation) => mutation.entityId === order.id || (mutation.entityType === "event" && mutation.payload.orderId === order.id));
      const differs = local ? JSON.stringify(local) !== JSON.stringify(order) : false;
      const localIsNewer = local ? local.updatedAt > order.updatedAt : false;
      if (local && differs && (pendingLocalMutation || localIsNewer)) {
        const reason = pendingLocalMutation ? "pending-local-mutation" : "local-newer-than-server";
        writes.push({ store: "conflicts", value: { id: `conflict:${order.id}:${order.version}:${order.updatedAt}`, orderId: order.id, detectedAt: new Date().toISOString(), reason, localOrder: local, remoteOrder: order } });
        continue;
      }
      writes.push({ store: "orders", value: { ...local, ...order, orderNo: order.orderNo ?? local?.orderNo, businessDate: order.businessDate ?? local?.businessDate, note: order.note ?? local?.note, payment: order.payment ?? local?.payment } });
    }
    await this.store.putMany(writes);
  }

  async applyOrderNumberAssignments(assignments: Array<{ orderId: string; orderNo: string }>): Promise<void> {
    if (assignments.length === 0) return;
    const byId = new Map(assignments.map((assignment) => [assignment.orderId, assignment.orderNo]));
    const orders = await this.store.list("orders");
    const writes = orders
      .filter((order) => byId.has(order.id))
      .map((order) => ({ store: "orders" as const, value: { ...order, orderNo: byId.get(order.id) } }));
    await this.store.putMany(writes);
  }

  async upsertRemoteEvents(events: OrderEvent[]): Promise<void> {
    const localOrders = new Map((await this.store.list("orders")).map((order) => [order.id, order]));
    const writes: Array<{ store: "events"; value: OrderEvent } | { store: "orders"; value: Order }> = events.map((event) => ({ store: "events" as const, value: event }));
    for (const event of events) {
      if (event.type !== "void" || !event.orderId) continue;
      const order = localOrders.get(event.orderId);
      if (!order || order.status === "voided") continue;
      writes.push({
        store: "orders" as const,
        value: { ...order, status: "voided" as const, updatedAt: event.occurredAt, version: order.version + 1 },
      });
    }
    await this.store.putMany(writes);
  }

  updateLocalOrder(order: Order): Promise<void> {
    return this.store.putMany([{ store: "orders", value: order }]);
  }

  async persistCompletedOrder(order: Order, mutation: OrderMutation, printJobs: LocalPrintJob[] = []): Promise<void> {
    if (mutation.entityId !== order.id || mutation.payload.id !== order.id) {
      throw new Error("Order and mutation identity must match");
    }
    await this.store.putMany([
      { store: "orders", value: order },
      { store: "outbox", value: mutation },
      ...printJobs.map((job) => ({ store: "printJobs" as const, value: job })),
    ]);
  }

  async persistEvent(event: OrderEvent, mutation?: EventMutation, printJobs: LocalPrintJob[] = []): Promise<void> {
    if (!event.id || !event.storeId || !event.actorId || (mutation && !event.deviceId)) throw new Error("Local event identity is required");
    await this.store.putMany([
      { store: "events", value: event },
      ...(mutation ? [{ store: "outbox" as const, value: mutation }] : []),
      ...printJobs.map((job) => ({ store: "printJobs" as const, value: job })),
    ]);
  }

  updatePrintJob(job: LocalPrintJob): Promise<void> {
    return this.store.putMany([{ store: "printJobs", value: job }]);
  }

  clearLocalData(): Promise<void> {
    return this.store.clear();
  }
}
